import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import {
  Check, Zap, Sparkles, Shield, Clock, CreditCard,
  ArrowRight, Package, Star
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.getElementById("razorpay-script")) { resolve(true); return; }
    const script = document.createElement("script");
    script.id = "razorpay-script";
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function PricingPage() {
  const { user, refreshUser } = useAuth();
  const { currency, toggleCurrency, formatPrice } = useCurrency();
  const [plans, setPlans] = useState({});
  const [scanPacks, setScanPacks] = useState({});
  const [razorpayKeyId, setRazorpayKeyId] = useState("");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [billingPeriod, setBillingPeriod] = useState("monthly");

  const currentPlan = user?.subscription?.plan || "trial";
  const currentBillingCycle = user?.subscription?.billing_cycle || "monthly";
  const isUnlimited = currentPlan === "unlimited";

  useEffect(() => {
    fetchPlans();
    fetchPaymentHistory();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API}/payments/plans`);
      setPlans(res.data.plans);
      setScanPacks(res.data.scan_packs);
      setRazorpayKeyId(res.data.razorpay_key_id);
    } catch { toast.error("Failed to load plans"); }
    finally { setLoading(false); }
  };

  const fetchPaymentHistory = async () => {
    try {
      const res = await axios.get(`${API}/payments/history`);
      setPaymentHistory(res.data);
    } catch { /* silent */ }
  };

  const handleSubscribe = async (planId) => {
    if (currentPlan === planId) return;
    setProcessing(planId);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) { toast.error("Payment gateway failed to load"); setProcessing(null); return; }

      const orderRes = await axios.post(`${API}/payments/create-order`, { plan_id: planId, currency });
      const { order_id, amount, currency: orderCurrency, plan } = orderRes.data;

      const options = {
        key: razorpayKeyId,
        amount,
        currency: orderCurrency,
        name: "ResumeAI",
        description: `${plan.name} Plan - ${plan.description}`,
        order_id,
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success(verifyRes.data.message);
            if (refreshUser) await refreshUser();
            fetchPaymentHistory();
          } catch (err) {
            toast.error(err.response?.data?.detail || "Payment verification failed");
          } finally { setProcessing(null); }
        },
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
        },
        theme: { color: "#3B82F6" },
        modal: {
          ondismiss: () => setProcessing(null),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        toast.error("Payment failed: " + (response.error?.description || "Please try again"));
        setProcessing(null);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initiate payment");
      setProcessing(null);
    }
  };

  const handleBuyPack = async (packId) => {
    setProcessing(packId);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) { toast.error("Payment gateway failed to load"); setProcessing(null); return; }

      const orderRes = await axios.post(`${API}/payments/create-addon-order`, { pack_id: packId, currency });
      const { order_id, amount, currency: addonCurrency, pack } = orderRes.data;

      const options = {
        key: razorpayKeyId,
        amount,
        currency: addonCurrency,
        name: "ResumeAI",
        description: `${pack.name} Add-on`,
        order_id,
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success(verifyRes.data.message);
            if (refreshUser) await refreshUser();
            fetchPaymentHistory();
          } catch (err) {
            toast.error(err.response?.data?.detail || "Payment verification failed");
          } finally { setProcessing(null); }
        },
        prefill: { name: user?.name || "", email: user?.email || "" },
        theme: { color: "#3B82F6" },
        modal: { ondismiss: () => setProcessing(null) },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        toast.error("Payment failed. Please try again.");
        setProcessing(null);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initiate payment");
      setProcessing(null);
    }
  };

  const u = user?.usage || {};
  const optLimit = u.opt_limit ?? u.scans_limit ?? 0;
  const optUsed = u.opt_used ?? u.scans_used ?? 0;
  const optAddon = u.opt_addon ?? u.addon_scans ?? 0;
  const scanLimit = u.scan_limit ?? u.scans_limit ?? 0;
  const scanUsed = u.scan_used ?? 0;
  const scanAddon = u.scan_addon ?? 0;
  const optRemaining = isUnlimited ? null : Math.max(0, optLimit - optUsed + optAddon);
  const scanRemaining = isUnlimited ? null : Math.max(0, scanLimit - scanUsed + scanAddon);

  const planOrder = ["trial", "base", "unlimited"];
  const isUpgrade = (planId) => planOrder.indexOf(planId) > planOrder.indexOf(currentPlan);
  const isCurrentPlan = (planId) => planId === currentPlan;

  const formatDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    catch { return ""; }
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: "4rem" }}><div className="processing-spinner" /></div>;
  }

  return (
    <div className="page-container fade-in" data-testid="pricing-page">
      <div className="page-header">
        <div>
          <h1>Plans & Billing</h1>
          <p className="page-subtitle">Choose the plan that fits your job search needs.</p>
        </div>
      </div>

      {/* Current Plan Status */}
      <div className="pricing-current" data-testid="pricing-current-plan">
        <div className="pricing-current-left">
          <div className="pricing-current-plan">
            <Zap size={16} />
            <span>Current Plan: <strong>{currentPlan === "trial" ? "Free Trial" : currentPlan === "base" ? "Base" : "Unlimited"}</strong></span>
          </div>
          <div className="pricing-current-usage" data-testid="pricing-current-usage">
            {isUnlimited ? (
              <span>Unlimited optimizations &amp; scans</span>
            ) : (
              <>
                <span data-testid="pricing-opt-remaining">{optRemaining} optimizations left</span>
                <span className="pricing-dot" />
                <span data-testid="pricing-scan-remaining">{scanRemaining} scans left</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Subscription Plans */}
      <div className="pricing-controls-row">
        <div className="pricing-period-toggle" data-testid="pricing-period-toggle">
          <button className={billingPeriod === "monthly" ? "active" : ""} onClick={() => setBillingPeriod("monthly")}>Monthly</button>
          <button className={billingPeriod === "annual" ? "active" : ""} onClick={() => setBillingPeriod("annual")}>
            Annual <span className="pricing-save-badge">Save 20%</span>
          </button>
        </div>
        <button className="lp-currency-toggle" onClick={toggleCurrency} data-testid="pricing-currency-toggle">
          <span className={currency === "USD" ? "active" : ""}>USD</span>
          <span className={currency === "INR" ? "active" : ""}>INR</span>
        </button>
      </div>
      <div className="pricing-plans" data-testid="pricing-plans">
        {/* Trial Card */}
        <div className={`pricing-card ${currentPlan === "trial" ? "current" : ""}`} data-testid="plan-trial">
          <div className="pricing-card-header">
            <span className="pricing-plan-name">Free Trial</span>
            {currentPlan === "trial" && <span className="pricing-current-badge">Current</span>}
          </div>
          <div className="pricing-price">
            <span className="pricing-amount">Free</span>
          </div>
          <ul className="pricing-features">
            <li><Check size={14} /> 50 optimizations included</li>
            <li><Check size={14} /> 50 scans included</li>
            <li><Check size={14} /> 7-day trial period</li>
            <li><Check size={14} /> ATS scoring &amp; optimization</li>
            <li><Check size={14} /> Cover letters &amp; recruiter emails</li>
          </ul>
          <button className="pricing-btn" disabled data-testid="btn-trial">
            {currentPlan === "trial" ? "Current Plan" : "Trial Used"}
          </button>
        </div>

        {/* Plans (DB-managed by admin) */}
        {Object.values(plans)
          .filter((p) => !p.archived && p.active !== false)
          .filter((p) => (billingPeriod === "annual" ? p.period === "annual" : (p.period || "monthly") !== "annual"))
          .sort((a, b) => (a.price_inr || 0) - (b.price_inr || 0))
          .map((plan) => {
            const baseId = plan.base_plan || plan.id;
            const period = plan.period || "monthly";
            const periodSuffix = period === "annual" ? "mo"
              : period === "monthly" ? "month"
              : period === "weekly" ? "week"
              : period === "quarterly" ? "quarter"
              : `${plan.duration_days || 30} days`;
            const scansNoun = period === "annual" ? "year"
              : period === "monthly" ? "month"
              : period === "weekly" ? "week"
              : period === "quarterly" ? "quarter"
              : `${plan.duration_days || 30} days`;
            const isUnlimitedPlan = (plan.scans_limit || 0) >= 999999;
            const current = currentPlan === baseId;
            const offerActive = !!(plan.offer && plan.offer.active);
            const reg = period === "annual"
              ? (currency === "USD" ? (plan.monthly_equivalent_usd ?? plan.price_usd / 12) / 100 : (plan.monthly_equivalent_inr ?? plan.price_inr / 12) / 100)
              : (currency === "USD" ? plan.price_usd / 100 : plan.price_inr / 100);
            const offerPrice = offerActive
              ? (currency === "USD" ? plan.offer.price_usd / 100 : plan.offer.price_inr / 100)
              : null;
            const total = currency === "USD" ? plan.price_usd / 100 : plan.price_inr / 100;
            return (
              <div key={plan.id} className={`pricing-card ${isUnlimitedPlan ? "featured" : ""} ${current ? "current" : ""}`} data-testid={`plan-${plan.id}`}>
                {isUnlimitedPlan && <div className="pricing-badge">{billingPeriod === "annual" ? "Best Value" : "Most Popular"}</div>}
                {offerActive && <div className="pricing-badge" style={{ background: "#10b981", top: isUnlimitedPlan ? 28 : undefined }} data-testid={`offer-badge-${plan.id}`}>{plan.offer.label || "Limited Offer"}</div>}
                <div className="pricing-card-header">
                  <span className="pricing-plan-name">{plan.name}</span>
                  {current && <span className="pricing-current-badge">Current</span>}
                </div>
                <div className="pricing-price">
                  {offerPrice != null && (
                    <span className="pricing-amount" style={{ textDecoration: "line-through", opacity: 0.45, fontSize: "1.1rem", marginRight: 8 }}>{formatPrice(reg)}</span>
                  )}
                  <span className="pricing-amount">{formatPrice(offerPrice != null ? offerPrice : reg)}</span>
                  <span className="pricing-period">/{periodSuffix}</span>
                </div>
                {period === "annual" && (
                  <div className="pricing-annual-note">{formatPrice(total)} billed annually</div>
                )}
                <ul className="pricing-features">
                  <li><Star size={14} /> {(plan.opt_credits ?? plan.scans_limit) >= 999999 ? "Unlimited optimizations" : `${plan.opt_credits ?? plan.scans_limit} optimizations per ${scansNoun}`}</li>
                  <li><Star size={14} /> {(plan.scan_credits ?? plan.scans_limit) >= 999999 ? "Unlimited scans" : `${plan.scan_credits ?? plan.scans_limit} scans per ${scansNoun}`}</li>
                  {plan.bonus_scans > 0 && <li><Check size={14} /> +{plan.bonus_scans} bonus scans (one-time)</li>}
                  {plan.description && <li><Check size={14} /> {plan.description}</li>}
                  <li><Check size={14} /> Free cover letters &amp; recruiter emails</li>
                  <li><Check size={14} /> Job application tracker</li>
                </ul>
                <button
                  className={`pricing-btn ${!current ? "primary" : ""}`}
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={current || processing === plan.id}
                  data-testid={`btn-${plan.id}`}
                >
                  {processing === plan.id ? "Processing..." : current ? "Current Plan" : `Choose ${plan.name}`}
                </button>
              </div>
            );
          })}
      </div>

      {/* Credit Packs */}
      <div className="pricing-section" data-testid="pricing-scan-packs">
        <h2><Package size={18} /> Credit Add-ons</h2>
        <p className="page-subtitle">Need more credits? Buy a one-time pack and use it anytime — no subscription required.</p>
        <div className="pricing-packs">
          {Object.entries(scanPacks).map(([id, pack]) => {
            const ctype = pack.credit_type || "scan";
            const count = pack.credits ?? pack.scans;
            const noun = ctype === "optimization" ? "optimizations" : "scans";
            return (
              <div key={id} className="pricing-pack-card" data-testid={`pack-${id}`}>
                <div className="pricing-pack-scans">{count}</div>
                <div className="pricing-pack-label" data-testid={`pack-type-${id}`}>{noun}</div>
                <div className="pricing-pack-price">{formatPrice(pack.price_usd / 100)}</div>
                <button
                  className="pricing-pack-btn"
                  onClick={() => handleBuyPack(id)}
                  disabled={processing === id}
                  data-testid={`buy-${id}`}
                >
                  {processing === id ? "Processing..." : "Buy Now"}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Payment History */}
      {paymentHistory.length > 0 && (
        <div className="pricing-section" data-testid="pricing-payment-history">
          <h2><CreditCard size={18} /> Payment History</h2>
          <div className="pricing-history-list">
            {paymentHistory.map(p => (
              <div key={p.id} className="pricing-history-item" data-testid={`payment-${p.id}`}>
                <div className="pricing-history-info">
                  <span className="pricing-history-type">
                    {p.type === "subscription"
                      ? `${(plans[p.plan_id] || {}).name || p.plan_id} Plan`
                      : `${(scanPacks[p.pack_id] || {}).name || p.pack_id}`
                    }
                  </span>
                  <span className="pricing-history-date">{formatDate(p.paid_at)}</span>
                </div>
                <span className="pricing-history-amount">
                  {p.currency === "INR" ? "₹" : "$"}{(p.amount / 100).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
